import { motion } from 'framer-motion'
import { ExternalLink, ArrowRight, Bot, ShieldCheck } from 'lucide-react'
import { Link } from 'react-router-dom'
import SEO from '../components/SEO'
import Footer from '../components/Footer'

function IconGithub({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.53 1.032 1.53 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"
      />
    </svg>
  )
}

export default function Skills() {
  return (
    <>
      <SEO
        title="Solarch Skills — AI Agent Capabilities & Developer Extensions"
        description="Official AI agent capabilities and developer skills for Solarch. Equip AI coding assistants and autonomous agents with verified tools, schemas, and workflows."
        canonical="/skills"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "Solarch Skills — AI Agent Capabilities & Developer Extensions",
          "url": "https://solarch.in/skills",
          "description": "Official AI agent capabilities and developer skills for Solarch. Equip AI coding assistants and autonomous agents with verified tools, schemas, and workflows.",
          "about": {
            "@id": "https://solarch.in/#software"
          },
          "publisher": {
            "@id": "https://solarch.in/#organization"
          }
        }}
      />

      <div className="min-h-screen bg-theme-body pt-14 flex flex-col">
        {/* Hero Section */}
        <section className="relative overflow-hidden pt-10 pb-8 sm:pt-14 sm:pb-10 lg:pt-16 lg:pb-12 text-center">
          <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="max-w-3xl mx-auto"
            >
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-theme mb-4">
                Solarch <span className="text-primary">Skills</span>
              </h1>

              <p className="text-base sm:text-lg text-theme-secondary leading-relaxed max-w-2xl mx-auto mb-8">
                Official developer skills and agent capabilities for the Solarch ecosystem, designed to equip AI coding assistants and autonomous agents with verified tools and workflows.
              </p>

              <div className="flex flex-wrap items-center justify-center gap-4">
                {/* Primary Button: View on GitHub */}
                <div className="p-1 rounded-full bg-theme-surface border border-[#3a2214] shadow-sm">
                  <a
                    href="https://github.com/xvertere-org/solarch-skills"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative inline-flex items-center gap-3 bg-primary text-white px-6 py-3 rounded-full font-semibold text-sm transition-all hover:brightness-110 active:scale-[0.98]"
                  >
                    <IconGithub className="h-4 w-4" />
                    <span>View on GitHub</span>
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-black/15 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-200">
                      <ExternalLink className="h-3 w-3" />
                    </div>
                  </a>
                </div>

                {/* Secondary Button: Explore SDKs */}
                <div className="p-1 rounded-full bg-theme-surface border border-[#3a2214] shadow-sm">
                  <Link
                    to="/docs/sdks"
                    className="group relative inline-flex items-center gap-3 bg-theme-surface text-theme px-6 py-3 rounded-full font-semibold text-sm transition-all hover:bg-theme-elevated hover:text-primary active:scale-[0.98]"
                  >
                    <span>Explore SDKs</span>
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-theme-elevated text-theme-secondary group-hover:text-primary group-hover:translate-x-0.5 transition-all duration-200">
                      <ArrowRight className="h-3.5 w-3.5" />
                    </div>
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Repository Status & Overview Cards */}
        <section className="pt-4 pb-16 sm:pt-6 sm:pb-20">
          <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
              
              {/* Card 1: What are Solarch Skills? */}
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 }}
                className="group relative bg-[#150d08] hover:bg-[#0a0603] rounded-2xl border border-[#3a2214] hover:border-primary/50 transition-all duration-300 flex flex-col p-6 sm:p-8 shadow-xl overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100 pointer-events-none" />
                
                <div className="relative z-10 flex flex-col">
                  <div className="mb-4 inline-flex p-3 rounded-xl bg-[#0a0603] border border-[#3a2214] group-hover:border-primary/50 transition-all duration-300 w-fit">
                    <Bot className="h-6 w-6 text-primary group-hover:scale-110 transition-transform duration-300" />
                  </div>
                  
                  <h2 className="mb-2.5 font-heading text-xl font-bold text-[#fdf3ec] tracking-tight group-hover:text-primary transition-colors">
                    What are Solarch Skills?
                  </h2>
                  <p className="text-sm sm:text-base leading-relaxed text-[#c9a894]">
                    Solarch Skills are standardized instruction sets, schemas, and execution workflows that allow AI coding assistants (such as Claude Desktop, Cursor, and agentic harnesses) to interact reliably with Solarch projects.
                  </p>
                </div>
              </motion.div>

              {/* Card 2: Official Source & Status */}
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.2 }}
                className="group relative bg-[#150d08] hover:bg-[#0a0603] rounded-2xl border border-[#3a2214] hover:border-primary/50 transition-all duration-300 flex flex-col p-6 sm:p-8 shadow-xl overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100 pointer-events-none" />
                
                <div className="relative z-10 flex flex-col">
                  <div className="mb-4 inline-flex p-3 rounded-xl bg-[#0a0603] border border-[#3a2214] group-hover:border-primary/50 transition-all duration-300 w-fit">
                    <ShieldCheck className="h-6 w-6 text-primary group-hover:scale-110 transition-transform duration-300" />
                  </div>
                  
                  <h2 className="mb-2.5 font-heading text-xl font-bold text-[#fdf3ec] tracking-tight group-hover:text-primary transition-colors">
                    Official Source &amp; Status
                  </h2>
                  <p className="text-sm sm:text-base leading-relaxed text-[#c9a894]">
                    All official Solarch skills and agent definitions are maintained in the repository at{' '}
                    <code className="text-primary font-mono text-xs px-2 py-0.5 rounded-md bg-[#0a0603] border border-primary/30">
                      xvertere-org/solarch-skills
                    </code>. Documentation and public catalog items will sync directly with repository releases.
                  </p>
                </div>
              </motion.div>

            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  )
}
